import { Component, OnInit } from '@angular/core';
import { SelectOption } from '@app/core/models/selector-option';
import {
  displayReadableMessageTypeEnum,
  MessageTypeEnum,
} from '@app/communications/models/enums/message-type.enum';
import { objectToOptionsWithDisplay } from '@app/core/helpers/object-to-options';
import { map, takeUntil } from 'rxjs/operators';
import { AutoUnsubscription } from '@app/shared/helpers/observable-helpers';
import { CommunicationsFacade } from '@app/communications/state/communications.facade';
import { Department } from '@app/communications/models/enums/department.enum';
import { displayRelationship } from '@app/core/models/personal-relationship';
import { UserMessagingMetadata } from '@app/communications/models/responses/user-messaging-metadata';
import { ClaimContactDetails } from '@app/communications/models/responses/claim-contact-details';
import { ClaimSmsModalFormService } from '@app/communications/components/claim-sms-modal/services/claim-sms-modal-form.service';
import { SystemContactVisibility } from '@app/communications/models/enums/system-contact-visibility.enum';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-claim-sms-modal',
  templateUrl: './claim-sms-modal.component.html',
  styleUrls: ['./claim-sms-modal.component.scss'],
  providers: [ClaimSmsModalFormService],
})
export class ClaimSmsModalComponent
  extends AutoUnsubscription
  implements OnInit
{
  claimCode: string;
  contactDetails$ = this.facade.claimContactDetails$;
  contactDetailsIsLoading$ = this.facade.claimContactDetailsIsLoading$;
  contactDetailsOptions: SelectOption[];
  isSendingSms$ = this.facade.isSendingSms$;
  isStartingConversation$ = this.facade.isStartingConversation$;
  isSending$ = combineLatest([
    this.isSendingSms$,
    this.isStartingConversation$,
  ]).pipe(
    map(
      ([isSendingSms, isStartingConversation]) =>
        isSendingSms || isStartingConversation,
    ),
  );
  form = this.formService.form;

  metadata$ = this.facade.userMessagingMetadata$;
  metadata: UserMessagingMetadata;
  senderOptions: SelectOption[];

  contactDetails: ClaimContactDetails[] = [];
  systemContactId?: number;
  public selectedSmsTemplatePreview$ =
    this.facade.selectedClaimSmsTemplatePreview$;
  messageTypeOptions: SelectOption[] = [];
  characterCount: number;
  shouldCreateConversation: boolean;
  conversationId: number | undefined;
  contactNumber: string;
  isCustom = false;
  hasValidContact = false;
  hasValidSystemContact = false;

  constructor(
    private facade: CommunicationsFacade,
    public formService: ClaimSmsModalFormService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.facade.loadUserMessagingMetadata();
    this.setupMessageTypes();
    this.handleMetadataChanges();
    this.handleClaimContactChanges();
    this.handleTemplateChanges();
    this.handleConversationChanges();
  }

  private setupMessageTypes() {
    const validSmsMessageTypes = new Set<MessageTypeEnum>();
    validSmsMessageTypes.add(MessageTypeEnum.InformationRequest);
    validSmsMessageTypes.add(MessageTypeEnum.PleaseCallMe);
    validSmsMessageTypes.add(MessageTypeEnum.CustomSMS);

    this.messageTypeOptions = objectToOptionsWithDisplay(MessageTypeEnum, (x) =>
      displayReadableMessageTypeEnum(x),
    ).filter((x) => validSmsMessageTypes.has(x.value as number));
  }

  private handleMetadataChanges() {
    this.metadata$.pipe(takeUntil(this.notifier)).subscribe((metadata) => {
      if (metadata.data) {
        this.metadata = metadata.data;
        this.systemContactId = this.metadata.contacts.find(
          (contact) => contact.departmentId === Department.Claims,
        ).systemContactId;
        this.senderOptions = metadata.data.contacts
          .filter(
            (contact) =>
              (contact.departmentId === Department.Claims ||
                contact.departmentId === Department.Registry) &&
              contact.isActive &&
              contact.visibilityLevel === SystemContactVisibility.SendMessages,
          )
          .map((contact) => ({
            value: contact.systemContactId,
            description: `${contact.name} ${contact.phoneNumber}`,
          }));
        this.hasValidSystemContact = this.senderOptions.length > 0;
        if (this.hasValidSystemContact) {
          this.formService.patchSender(+this.senderOptions[0].value);
        }
      }
    });
  }

  private handleClaimContactChanges() {
    this.contactDetails$
      .pipe(takeUntil(this.notifier))
      .subscribe((contactDetails) => {
        if (contactDetails) {
          this.contactDetails = contactDetails;
          this.contactDetailsOptions = contactDetails
            .filter((contact) => contact.isValidPhoneNumber)
            .map((details) => ({
              value: details.claimsContactId,
              description: `${details.name} ${
                details.surname
              } (${displayRelationship(details.relationship)}) ${
                details.phoneNumber
              }`,
            }));
          this.hasValidContact = this.contactDetailsOptions.length > 0;
          if (this.hasValidContact) {
            this.formService.patchClaimContact(
              +this.contactDetailsOptions[0].value,
            );
          }
        }
      });
  }

  private handleConversationChanges() {
    this.form.valueChanges
      .pipe(takeUntil(this.notifier))
      .subscribe((formValue) => {
        const contactId = formValue.claimContact;
        const systemContactId = formValue.sender;
        const contact = this.contactDetails.find(
          (contact) => contact.claimsContactId === contactId,
        );
        const systemContact = this.metadata.contacts?.find(
          (contact) => contact.systemContactId === systemContactId,
        );
        const conversation = contact?.conversations.find(
          (conversation) =>
            (conversation.systemContactId === systemContactId ||
              conversation.systemContactPhoneNumber ==
                systemContact.phoneNumber) &&
            (conversation.hasSMSMessages || conversation.hasWhatsAppMessages),
        );
        this.shouldCreateConversation = !conversation;
        this.conversationId = conversation?.conversationId;
      });
  }

  private handleTemplateChanges() {
    this.form.controls.messageType.valueChanges
      .pipe(takeUntil(this.notifier))
      .subscribe((messageType) => {
        if (messageType) {
          this.isCustom = messageType == MessageTypeEnum.CustomSMS;
          if (!this.isCustom) {
            this.facade.loadClaimSmsMessageTemplatePreview(
              messageType,
              this.claimCode,
            );
          }
        }
      });

    this.form.controls.message.valueChanges
      .pipe(takeUntil(this.notifier))
      .subscribe((message) => {
        this.characterCount = message?.length ?? 0;
      });
    this.selectedSmsTemplatePreview$
      .pipe(takeUntil(this.notifier))
      .subscribe((messagePreview) => {
        this.formService.patchMessage(messagePreview);
      });
  }

  clear() {
    this.formService.clear();
    this.facade.clearClaimSmsMessageTemplatePreview();
  }

  submit() {
    this.facade.sendClaimSms(
      this.formService.populateRequestModelFromForm(
        this.claimCode,
        this.conversationId,
      ),
      this.shouldCreateConversation,
    );
  }

  requestCustomSMS() {
    const request = this.formService.populateRequestModelFromForm(
      this.claimCode,
      this.conversationId,
    );
    this.facade.requestCustomClaimSms(request, this.shouldCreateConversation);
  }
}
